import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly erreur: Error | null;
}

/**
 * Filet de dernier recours.
 *
 * Sans lui, une erreur de rendu — une propriété absente dans une ligne suffit —
 * vide l'écran sans un mot. Ici l'utilisateur voit ce qui s'est passé et peut
 * recharger, et la console garde la trace technique complète.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { erreur: null };

  static getDerivedStateFromError(erreur: Error): State {
    return { erreur };
  }

  componentDidCatch(erreur: Error, infos: ErrorInfo): void {
    // La trace complète reste dans la console : l'écran ne doit pas la résumer,
    // mais il ne doit pas non plus la cacher.
    console.error('[espace prestataire] rendu interrompu', erreur, infos.componentStack);
  }

  private readonly recharger = () => {
    globalThis.location.reload();
  };

  render(): ReactNode {
    const { erreur } = this.state;
    if (!erreur) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--app-background)] p-6">
        <div
          className="bg-white rounded-lg shadow-sm border border-red-200 p-8 max-w-xl"
          role="alert"
        >
          <h1 className="text-lg font-semibold text-slate-800 mb-2">
            L’affichage s’est interrompu
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            Une erreur est survenue pendant le rendu de cette page. Vos données ne sont pas en cause
            : rien n’a été enregistré ni supprimé côté serveur.
          </p>
          <p className="text-xs text-slate-500 mb-6 font-mono break-words">{erreur.message}</p>
          <button
            type="button"
            onClick={this.recharger}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
          >
            Recharger la page
          </button>
        </div>
      </div>
    );
  }
}
